import type { FC, ReactNode } from "react"
import React, { useMemo } from "react"
import {
  Stack,
  Text,
  TableContainer,
  Table,
  Box,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  HStack,
  Link,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
} from "@chakra-ui/react"
import isEqual from "lodash.isequal"

import type {
  SimulationResult,
  SimulationResultType,
} from "proposal/components/simulation/SimulationResultBadge"
import SimulationResultBadge from "proposal/components/simulation/SimulationResultBadge"
import type {
  TransactionSimulationV2,
  SimulationStatus,
  Maybe,
  ThreatAnalysis,
} from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import type { CreateProposalFormValues } from "createProposal/types/form"
import type { RewardRecipient } from "createProposal/action/TransferArbitrumErc20Action"
import { recipeReadableMap } from "createProposal/action/types"
import { EXTERNAL_ROUTES } from "common/constants/routes"
import { ArrowUpRight } from "ui/components/icons/font-awesome/ArrowUpRight"
import { pluralize } from "common/helpers/string"
import { getDateFromTimestamp, getTimestampFromIetf } from "common/helpers/date"
import { DATE_FORMATS } from "common/constants/date"
import type { ProposalExecutableCall } from "proposal/components/simulation/ProposalImpactOverview"
import ConditionalRender from "common/components/ConditionalRender"
import { shortenIfAddress } from "web3/helpers/address"
import CopyButton from "common/components/CopyButton"

type Action = {
  recipe?: ExecutableCallType
  rewardsRecipients?: RewardRecipient[]
  sellToken?: { symbol: string }
}

type MappedSimulation = {
  action: number
  type?: ExecutableCallType
  simulation: TransactionSimulationV2
}

type Props = {
  threatAnalysis: ThreatAnalysis | null
  threatAnalysisResult?: SimulationResultType
  simulationsResult?: SimulationResultType
  simulations: TransactionSimulationV2[] | null
  values?: CreateProposalFormValues
  executableCalls?: ProposalExecutableCall[]
  createdAt?: string
}

const ProposalImpactDetails: FC<Props> = ({
  threatAnalysis,
  simulations,
  threatAnalysisResult,
  simulationsResult,
  values,
  executableCalls,
  createdAt,
}) => {
  const createdAtLabel = useMemo(() => {
    if (!createdAt) return null

    const timestamp = getTimestampFromIetf(createdAt)
    const date = getDateFromTimestamp(timestamp)

    return date.format(DATE_FORMATS.statusHistoryFullYear)
  }, [createdAt])

  const actionsSimulated = executableCalls?.length ?? 0

  return (
    <Stack spacing={3}>
      {actionsSimulated ? (
        <Text fontSize="sm">
          {actionsSimulated} {pluralize(actionsSimulated, "action", "actions")}{" "}
          simulated {createdAtLabel ? <>• {createdAtLabel}</> : ""}
        </Text>
      ) : null}
      <Accordion allowMultiple allowToggle>
        {simulations ? (
          <SimulationsSection
            executableCalls={executableCalls}
            result={simulationsResult}
            simulations={simulations}
            values={values}
          />
        ) : null}

        <ThreatAnalysisSection
          result={threatAnalysisResult}
          threatAnalysis={threatAnalysis}
        />
      </Accordion>
    </Stack>
  )
}

type ThreatAnalysisSectionProps = {
  threatAnalysis?: Maybe<ThreatAnalysis>
  result?: SimulationResultType
}
const ThreatAnalysisSection: FC<ThreatAnalysisSectionProps> = ({
  threatAnalysis,
  result,
}) => {
  const threatAnalysisEvents =
    threatAnalysis?.actionsData?.flatMap(
      (actionsData) =>
        actionsData?.events?.filter((event) => event.severity !== "None") ?? [],
    ) ?? null

  return (
    <Section
      learnMoreLink={{
        url: EXTERNAL_ROUTES.simulations.hexagate(),
        label: "Learn more about Hexagate",
      }}
      result={result}
      title="Hexagate simulations"
    >
      {threatAnalysisEvents && threatAnalysisEvents.length > 0 ? (
        <TableContainer
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          width="full"
        >
          <Table>
            <Thead>
              <Tr>
                <Th px={3} py={3}>
                  Details
                </Th>
                <Th isNumeric pr={10} py={3}>
                  Result
                </Th>
              </Tr>
            </Thead>

            <Tbody>
              {threatAnalysisEvents.map((event, idx) => (
                <Tr key={`threat-analysis-${idx}`}>
                  <Th px={3} py={3}>
                    {event.eventType}: {event.description}
                  </Th>
                  <Th isNumeric px={3} py={3}>
                    {event.severity}
                    <SimulationResultBadge
                      result={event.severity as SimulationResultType}
                    />
                  </Th>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ) : (
        <Box p={3}>
          <Text>No threats detected</Text>
        </Box>
      )}
    </Section>
  )
}

type SimulationLinkProps = {
  result: SimulationStatus
  url?: string
}

const SimulationText: React.FC<SimulationLinkProps> = ({ result, url }) => {
  const displayLinkIcon = url && url.trim() !== ""

  return (
    <HStack align="center" justify="end">
      <SimulationResultBadge result={result as unknown as SimulationResult} />

      {displayLinkIcon ? (
        <Icon as={ArrowUpRight} color="gray.500" h="16px" w="16px" />
      ) : (
        <Box width="16px" />
      )}
    </HStack>
  )
}

type SimulationsSectionProps = {
  simulations: TransactionSimulationV2[] | null
  result?: SimulationResultType
  executableCalls?: ProposalExecutableCall[]
  values?: CreateProposalFormValues
}
const SimulationsSection: FC<SimulationsSectionProps> = ({
  simulations,
  executableCalls,
  values,
  result,
}) => {
  function mapSimulationsToActions(
    actions: Action[],
    simulations: TransactionSimulationV2[] | null | undefined,
  ): MappedSimulation[] | undefined {
    if (!simulations) return undefined

    const fullAccumulatedArray = actions.reduce(
      (acc: MappedSimulation[], action, actionIndex) => {
        const makeBaseObject = (index: number) => ({
          action: actionIndex,
          type: action.recipe,
          simulation: simulations[index],
        })

        switch (action.recipe) {
          case ExecutableCallType.Custom:
            return [...acc, makeBaseObject(actionIndex)]

          case ExecutableCallType.Swap:
            const objectsForSwap = action.sellToken?.symbol === "ETH" ? 4 : 3

            return [
              ...acc,
              ...Array.from({ length: objectsForSwap }, (_) =>
                makeBaseObject(actionIndex),
              ),
            ]

          case ExecutableCallType.Nativetransfer:
          case ExecutableCallType.Erc20transfer:
          case ExecutableCallType.Erc20transferarbitrum:
            const additionalObjectsCount =
              action.rewardsRecipients && action.rewardsRecipients.length > 0
                ? action.rewardsRecipients.length
                : 0

            return [
              ...acc,
              ...Array.from({ length: 1 + additionalObjectsCount }, (_) =>
                makeBaseObject(actionIndex),
              ),
            ]

          default:
            return [...acc, makeBaseObject(0)]
        }
      },
      [],
    )

    const accumulatedArray = fullAccumulatedArray.reduce<MappedSimulation[]>(
      (acc, current) => {
        const isDuplicate = acc.some((item) => item.action === current.action)

        if (!isDuplicate) {
          acc.push(current)
        }

        return acc
      },
      [],
    )

    if (accumulatedArray.length !== simulations.length) return undefined

    return accumulatedArray
  }

  const mappedSimulations = useMemo(() => {
    if (values?.actions) {
      return mapSimulationsToActions(values.actions, simulations)
    }
  }, [values?.actions, simulations])

  const tableType: "mappedSimulations" | "executableCalls" | "general" =
    useMemo(() => {
      if (mappedSimulations && mappedSimulations.length > 0) {
        return "mappedSimulations"
      }

      if (executableCalls && executableCalls.length > 0) {
        return "executableCalls"
      }

      return "general"
    }, [mappedSimulations, executableCalls])

  return (
    <Section
      learnMoreLink={{
        url: EXTERNAL_ROUTES.simulations.tenderly(),
        label: "Learn more about Tenderly",
      }}
      result={result}
      title="Tenderly simulations"
    >
      <TableContainer
        background="white"
        border="0px"
        borderColor="gray.200"
        width="full"
      >
        <ConditionalRender condition={tableType === "mappedSimulations"}>
          <MappedSimulationsTable mappedSimulations={mappedSimulations ?? []} />
        </ConditionalRender>

        <ConditionalRender condition={tableType === "executableCalls"}>
          <ExecutableCallsTable
            executableCalls={executableCalls ?? []}
            simulations={simulations ?? []}
          />
        </ConditionalRender>

        <ConditionalRender condition={tableType === "general"}>
          <GeneralTable simulations={simulations ?? []} />
        </ConditionalRender>
      </TableContainer>
    </Section>
  )
}

type MappedSimulationsTableProps = {
  mappedSimulations: MappedSimulation[]
}
const MappedSimulationsTable: FC<MappedSimulationsTableProps> = ({
  mappedSimulations,
}) => {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th px={3} py={3}>
            #
          </Th>
          <Th px={3} py={3}>
            Type
          </Th>
          <Th isNumeric pr={10} py={3}>
            Simulation
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {mappedSimulations.map((mappedSimulation, idx) => {
          return (
            <Tr key={`simulation-${idx}`}>
              <Td px={3} py={3}>
                <Text>{mappedSimulation.action + 1}</Text>
              </Td>
              <Td px={3} py={3}>
                <Text>
                  {mappedSimulation.type
                    ? recipeReadableMap[mappedSimulation.type]
                    : ""}
                </Text>
              </Td>
              <Td isNumeric px={3} py={3}>
                {mappedSimulation.simulation ? (
                  <SimulationLink
                    result={
                      mappedSimulation.simulation.result as SimulationStatus
                    }
                    url={mappedSimulation.simulation.publicURI}
                  />
                ) : null}
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

type GeneralTableProps = {
  simulations: TransactionSimulationV2[]
}
const GeneralTable: FC<GeneralTableProps> = ({ simulations }) => {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th px={3} py={3}>
            #
          </Th>
          <Th isNumeric pr={10} py={3}>
            Simulation
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {simulations?.map((simulation, idx) => {
          return (
            <Tr key={`simulation-${idx}`}>
              <Td px={3} py={3}>
                <Text>Action {idx + 1}</Text>
              </Td>
              <Td isNumeric px={3} py={3}>
                <SimulationLink
                  result={simulation.result as SimulationStatus}
                  url={simulation.publicURI}
                />
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

type ExecutableCallsTableProps = {
  executableCalls: ProposalExecutableCall[]
  simulations: TransactionSimulationV2[]
}
const ExecutableCallsTable: FC<ExecutableCallsTableProps> = ({
  executableCalls,
  simulations,
}) => {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th maxWidth="10%" minWidth="10%" px={3} py={3} whiteSpace="nowrap">
            #
          </Th>
          <Th maxWidth="30%" minWidth="30%" px={3} py={3}>
            Contract
          </Th>
          <Th maxWidth="30%" minWidth="30%" px={3} py={3}>
            Function
          </Th>
          <Th isNumeric maxWidth="30%" minWidth="30%" pr={10} py={3}>
            Simulation
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {simulations?.map((simulation, idx) => {
          const executableCall = executableCalls?.[idx]

          return (
            <Tr key={`simulation-${idx}`}>
              <Td
                maxWidth="10%"
                minWidth="10%"
                px={3}
                py={3}
                whiteSpace="nowrap"
              >
                <Text fontSize="sm">{idx + 1}</Text>
              </Td>
              <Td maxWidth="30%" minWidth="30%" px={3} py={3}>
                <Text fontSize="sm">
                  {executableCall.target ? (
                    <Stack align="center" direction="row">
                      <Text>{shortenIfAddress(executableCall.target)}</Text>
                      <CopyButton
                        size={8}
                        tooltipLabel="Target contract address"
                        value={executableCall.target}
                      />
                    </Stack>
                  ) : (
                    "-"
                  )}
                </Text>
              </Td>
              <Td maxWidth="30%" minWidth="30%" px={3} py={3}>
                <Text fontSize="sm">{executableCall.signature ?? "-"}</Text>
              </Td>
              <Td isNumeric maxWidth="30%" minWidth="30%" px={3} py={3}>
                <SimulationLink
                  result={simulation.result as SimulationStatus}
                  url={simulation.publicURI}
                />
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

const SimulationLink: React.FC<SimulationLinkProps> = ({ result, url }) => {
  // TODO(@nicolas): !isEqual(result as string, "unknown") should be replaced to
  // result !== SimulationStatus.Unknown when this state is available in the API
  const hasValidUrl =
    url && url.trim() !== "" && !isEqual(result as string, "unknown")

  return (
    <>
      {hasValidUrl ? (
        <Link isExternal _hover={{ textDecoration: "none" }} href={url}>
          <SimulationText result={result} url={url} />
        </Link>
      ) : (
        <SimulationText result={result} />
      )}
    </>
  )
}

type SectionProps = {
  title: string
  children: ReactNode
  learnMoreLink?: { url: string; label: string }
  result?: SimulationResultType
}
const Section: FC<SectionProps> = ({
  title,
  children,
  learnMoreLink,
  result,
}) => {
  return (
    <AccordionItem
      bgColor="white"
      borderColor="gray.200"
      borderRadius="md"
      borderWidth="1px"
      mb={3}
    >
      <AccordionButton
        borderBottomColor="gray.200"
        borderBottomWidth="1px"
        px={3}
        py={3}
      >
        <AccordionIcon />
        <HStack as="span" direction="row" flex="1" textAlign="left">
          <Text fontWeight="bold">{title}</Text>
          {result ? <SimulationResultBadge iconOnly result={result} /> : null}
        </HStack>

        {learnMoreLink ? (
          <Link
            isExternal
            _hover={{
              textDecoration: "underline",
              textDecorationColor: "purple.500",
            }}
            href={learnMoreLink.url}
            textAlign="right"
          >
            <Text
              color="purple.500"
              display={{ base: "none", md: "block" }}
              fontSize="sm"
            >
              {learnMoreLink.label}
            </Text>
            <Text
              color="purple.500"
              display={{ base: "block", md: "none" }}
              fontSize="sm"
            >
              Learn more
            </Text>
          </Link>
        ) : undefined}
      </AccordionButton>
      <AccordionPanel p={0}>{children}</AccordionPanel>
    </AccordionItem>
  )
}

export default ProposalImpactDetails
